import { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export const ROLE_TYPES = {
  DRIVER: 'driver',
  HOST: 'conductor',
  STATION_SUPERUSER: 'station_superuser',
  OVERSEER: 'overseer',
  PASSENGER: 'passenger'
};

export const isDriverRole = (role) => role === ROLE_TYPES.DRIVER;
export const isHostRole = (role) => role === ROLE_TYPES.HOST;
export const isStaffRole = (role) => [ROLE_TYPES.HOST, ROLE_TYPES.DRIVER, ROLE_TYPES.STATION_SUPERUSER, ROLE_TYPES.OVERSEER].includes(role);
export const isAdminRole = (role) => [ROLE_TYPES.STATION_SUPERUSER, ROLE_TYPES.OVERSEER].includes(role);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const USER_STORAGE_KEY = 'panashe_user';

// Routes that should NOT trigger a forced logout on 401
const PUBLIC_PATH_PREFIXES = ['/', '/search', '/track', '/login', '/ticket', '/luggage-ticket'];
const isOnPublicRoute = () => {
  const p = window.location.pathname;
  return PUBLIC_PATH_PREFIXES.some(pref => p === pref || (pref !== '/' && p.startsWith(pref)));
};

const persistUser = (nextUser) => {
  if (nextUser) {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
  } else {
    localStorage.removeItem(USER_STORAGE_KEY);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem(USER_STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      return null;
    }
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const interceptorRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const activeRequestId = ++requestIdRef.current;

      const resolveCurrentUser = async () => {
        try {
          const response = await axios.get(`${API_URL}/auth/me`);
          if (activeRequestId !== requestIdRef.current) return;
          setUser(response.data);
          persistUser(response.data);
        } catch (error) {
          if (activeRequestId !== requestIdRef.current) return;
          console.error('Failed to fetch user:', error);
          logout();
        } finally {
          if (activeRequestId === requestIdRef.current) {
            setLoading(false);
          }
        }
      };

      resolveCurrentUser();
      return () => {
        requestIdRef.current += 1;
      };
    }

    setLoading(false);
    return undefined;
  }, [token]);

  // Axios response interceptor — graceful 401 handling (token expiry / revoked)
  useEffect(() => {
    if (interceptorRef.current !== null) {
      axios.interceptors.response.eject(interceptorRef.current);
    }
    interceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || '';
        // Only trigger logout on protected /api/* 401s, never on login/register endpoints
        const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');
        if (status === 401 && !isAuthEndpoint && token) {
          // Token expired or revoked
          localStorage.removeItem('token');
          localStorage.removeItem(USER_STORAGE_KEY);
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
          setUser(null);
          requestIdRef.current += 1;
          if (!isOnPublicRoute()) {
            toast.error('Session expired. Please log in again.');
            // Defer navigation to avoid race with current render
            setTimeout(() => { window.location.href = '/login'; }, 200);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
        interceptorRef.current = null;
      }
    };
  }, [token]);

  const fetchUser = async () => {
    const activeRequestId = ++requestIdRef.current;
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      if (activeRequestId !== requestIdRef.current) return;
      setUser(response.data);
      persistUser(response.data);
    } catch (error) {
      if (activeRequestId !== requestIdRef.current) return;
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      if (activeRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;

    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    persistUser(userData);

    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;

    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(newUser);
    persistUser(newUser);

    return newUser;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem(USER_STORAGE_KEY);
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    requestIdRef.current += 1;
  };

  const isStaff = () => {
    return user && isStaffRole(user.role);
  };

  const isAdmin = () => {
    return user && isAdminRole(user.role);
  };

  const isDriver = () => {
    return user && isDriverRole(user.role);
  };

  const isHost = () => {
    return user && isHostRole(user.role);
  };

  const isOverseer = () => {
    return user && user.role === ROLE_TYPES.OVERSEER;
  };

  const hasDuty = (duty) => {
    if (!user) return false;
    if (user.role === ROLE_TYPES.OVERSEER) return true;
    if (user.role === ROLE_TYPES.STATION_SUPERUSER) {
      return (user.delegated_duties || []).includes(duty);
    }
    return false;
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      isStaff,
      isAdmin,
      isDriver,
      isHost,
      isOverseer,
      hasDuty
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
